import { useEffect, useState } from "react";
import { useInView } from "../lib/useInView";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Home, Plus, Pencil, Trash2, Loader2, Star, PauseCircle, PlayCircle,
  CalendarClock, Sparkles,
} from "lucide-react";
import AdminAddPropertyBookingDialog from "./AdminAddPropertyBookingDialog";
import { PropertyAutopayToggle, RecurringExtrasList } from "./PropertyBillingControls";

/**
 * AdminPropertiesPanel — iter255 (2026-08-24)
 *
 * Compact admin panel embedded inside each customer row so admins can
 * see and manage all properties belonging to that customer without
 * leaving the Customers list.  Same CRUD contract as the customer's
 * `/api/me/properties` endpoints — mounts via the mirror admin routes
 * `/api/admin/customers/{user_id}/properties[/{property_id}]`.
 */

const YARD_OPTIONS = [
  { key: "front", label: "Front" },
  { key: "back",  label: "Back" },
  { key: "both",  label: "Full" },
];

const EMPTY_FORM = { label: "", address: "", square_feet: "", yard_area: "both", notes: "" };

export default function AdminPropertiesPanel({ userId, testIdPrefix = "admin-properties" }) {
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [schedules, setSchedules] = useState({});   // property_id → schedule
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(null);   // property_id | "new" | null
  const [form, setForm] = useState(EMPTY_FORM);
  // iter257 — Pause modal state.  Kept scoped to the specific
  // property_id + schedule so multi-property customers can't have
  // a pause on Home accidentally hit Rental.
  const [pauseFor, setPauseFor] = useState(null);   // { property_id, label, address, schedule }
  const [pauseForm, setPauseForm] = useState({ mode: "date", resume_date: "" });
  // iter259 — full "Add Property & Book Service" workflow
  const [addBookOpen, setAddBookOpen] = useState(false);
  // iter260 — lazy-load: only fetch once this row is near the viewport so
  // a 500-customer list never floods the API.
  const [rootRef, visible] = useInView();


  const refresh = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [propsResp, schedResp] = await Promise.all([
        api.get(`/admin/customers/${userId}/properties`),
        api.get(`/admin/customers/${userId}/recurring/list`).catch(
          () => ({ data: { schedules: [] } }),
        ),
      ]);
      setProperties(propsResp?.data?.properties || []);
      const byPid = {};
      for (const s of (schedResp?.data?.schedules || [])) byPid[s.property_id] = s;
      setSchedules(byPid);
    } catch {
      setProperties([]);
      setSchedules({});
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (visible) refresh(); /* eslint-disable-line */ }, [userId, visible]);

  const openNew = () => { setForm(EMPTY_FORM); setEditing("new"); };
  const openEdit = (p) => {
    setForm({
      label: p.label || "",
      address: p.address || "",
      square_feet: p.square_feet ?? "",
      yard_area: p.yard_area || "both",
      notes: p.notes || "",
    });
    setEditing(p.property_id);
  };
  const closeDialog = () => { setEditing(null); setForm(EMPTY_FORM); };

  const save = async () => {
    if (!form.address?.trim()) return toast.error("Address is required");
    const sqft = Number.parseInt(form.square_feet, 10);
    if (!Number.isFinite(sqft) || sqft <= 0) return toast.error("Enter a valid lawn size in sqft");
    setBusy("save");
    const body = {
      label: (form.label || "").trim() || undefined,
      address: form.address.trim(),
      square_feet: sqft,
      yard_area: form.yard_area,
      notes: (form.notes || "").trim() || null,
    };
    try {
      if (editing === "new") {
        await api.post(`/admin/customers/${userId}/properties`, body);
        toast.success("Property added to customer.");
      } else {
        await api.patch(`/admin/customers/${userId}/properties/${editing}`, body);
        toast.success("Property updated.");
      }
      await refresh();
      closeDialog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save property");
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async (p) => {
    if (!window.confirm(`Delete property "${p.label || p.address}"?\n\nOnly properties with no active or completed bookings can be removed.`)) return;
    setBusy(p.property_id);
    try {
      await api.delete(`/admin/customers/${userId}/properties/${p.property_id}`);
      toast.success("Property removed.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove property");
    } finally {
      setBusy(null);
    }
  };

  // iter257 — Pause/resume the recurring schedule for ONE property.
  const openPause = (p) => {
    const s = schedules[p.property_id];
    setPauseFor({ property_id: p.property_id, label: p.label, address: p.address, schedule: s });
    setPauseForm({
      mode: s?.resume_at ? "date" : "date",
      resume_date: s?.resume_at || "",
    });
  };
  const closePause = () => { setPauseFor(null); };

  const submitPause = async () => {
    if (!pauseFor) return;
    if (!pauseForm.resume_date) return toast.error("Pick a resume date");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [y, m, d] = pauseForm.resume_date.split("-").map(Number);
    const picked = new Date(y, m - 1, d);
    if (picked <= today) return toast.error("Resume date must be in the future");
    setBusy("pause");
    try {
      await api.post(`/admin/customers/${userId}/recurring/pause`, {
        property_id: pauseFor.property_id,
        resume_date: pauseForm.resume_date,
      });
      toast.success(`${pauseFor.label || "Property"} paused until ${pauseForm.resume_date}.`);
      await refresh();
      closePause();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pause service");
    } finally {
      setBusy(null);
    }
  };

  const doResume = async (p) => {
    setBusy(p.property_id);
    try {
      await api.post(`/admin/customers/${userId}/recurring/resume`, {
        property_id: p.property_id,
      });
      toast.success(`${p.label || "Property"} recurring service resumed.`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume service");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div ref={rootRef} className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50/50 px-3 py-2.5"
         data-testid={`${testIdPrefix}-${userId}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-700 flex items-center gap-1.5">
          <Home className="w-3 h-3" /> Properties ({properties.length})
        </div>
        <div className="flex gap-1.5 flex-wrap">
        <Button size="sm" onClick={openNew}
                className="h-7 rounded-full bg-[#047857] hover:bg-[#065F46] text-white text-[11px] px-2.5"
                data-testid={`${testIdPrefix}-add-${userId}`}>
          <Plus className="w-3 h-3 mr-1" /> Add Property
        </Button>
        <Button size="sm" onClick={() => setAddBookOpen(true)}
                className="h-7 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-[11px] px-2.5"
                data-testid={`${testIdPrefix}-add-and-book-${userId}`}>
          <Sparkles className="w-3 h-3 mr-1" /> Add Property &amp; Service
        </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-3"><Loader2 className="w-4 h-4 animate-spin text-emerald-700" /></div>
      ) : properties.length === 0 ? (
        <div className="mt-2 text-[11px] text-gray-500">No properties yet.</div>
      ) : (
        <div className="mt-2 grid gap-1.5">
          {properties.map((p) => {
            const sched = schedules[p.property_id];
            const isActive = sched && sched.status === "active";
            const isPaused = sched && sched.status === "paused";
            return (
            <div key={p.property_id}
                 className="bg-white border border-gray-200 rounded-xl px-2.5 py-2"
                 data-testid={`${testIdPrefix}-row-${p.property_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[12px] font-extrabold text-gray-900 truncate">{p.label || "Property"}</span>
                    {p.is_default && (
                      <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-emerald-50 text-[#047857] border border-emerald-200 text-[9px] font-extrabold uppercase tracking-wider">
                        <Star className="w-2 h-2" /> Default
                      </span>
                    )}
                    {isActive && (
                      <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[9px] font-extrabold uppercase tracking-wider"
                            data-testid={`${testIdPrefix}-status-${p.property_id}`}>
                        Active · {(sched?.frequency_label || sched?.frequency || "").replaceAll("_", " ")}
                      </span>
                    )}
                    {isPaused && (
                      <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[9px] font-extrabold uppercase tracking-wider"
                            data-testid={`${testIdPrefix}-status-${p.property_id}`}>
                        <PauseCircle className="w-2.5 h-2.5" />
                        Paused{sched?.resume_at ? ` · Resumes ${sched.resume_at}` : ""}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-600 truncate">{p.address}</div>
                  <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    {(p.square_feet || 0).toLocaleString()} sq ft · {p.yard_area === "both" ? "Full yard" : `${p.yard_area} yard`}
                    {sched?.locked_price ? ` · $${sched.locked_price.toFixed(2)}/visit` : ""}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                <button
                  onClick={() => openEdit(p)}
                  className="p-1.5 rounded-md hover:bg-gray-100"
                  title="Edit"
                  data-testid={`${testIdPrefix}-edit-${p.property_id}`}
                >
                  <Pencil className="w-3.5 h-3.5 text-gray-600" />
                </button>
                <button
                  onClick={() => doDelete(p)}
                  disabled={busy === p.property_id}
                  className="p-1.5 rounded-md hover:bg-red-50"
                  title="Delete"
                  data-testid={`${testIdPrefix}-delete-${p.property_id}`}
                >
                  {busy === p.property_id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin text-red-600" />
                    : <Trash2 className="w-3.5 h-3.5 text-red-600" />}
                </button>
              </div>
              </div>
              <RecurringExtrasList schedule={sched} userId={userId} mode="admin"
                                   propertyId={p.property_id} onChanged={refresh} />
              <div className="mt-2">
                <PropertyAutopayToggle property={p} userId={userId} mode="admin" onChanged={refresh} />
              </div>
              {/* iter257 — per-property Pause / Resume Service row. Only
                  active or paused schedules render buttons; if no
                  schedule exists yet the row is hidden. */}
              {(isActive || isPaused) && (
                <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap gap-1.5"
                     data-testid={`${testIdPrefix}-recurring-actions-${p.property_id}`}>
                  {isActive && (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => openPause(p)}
                      className="h-7 rounded-full text-[11px] px-2.5 border-amber-300 text-amber-800 hover:bg-amber-50"
                      data-testid={`${testIdPrefix}-pause-${p.property_id}`}
                    >
                      <PauseCircle className="w-3 h-3 mr-1" /> Pause Service
                    </Button>
                  )}
                  {isPaused && (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => doResume(p)}
                      disabled={busy === p.property_id}
                      className="h-7 rounded-full text-[11px] px-2.5 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                      data-testid={`${testIdPrefix}-resume-${p.property_id}`}
                    >
                      {busy === p.property_id
                        ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        : <PlayCircle className="w-3 h-3 mr-1" />}
                      Resume Service
                    </Button>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* iter257 — Admin Pause Service modal (per property) */}
      <Dialog open={!!pauseFor} onOpenChange={(o) => !o && closePause()}>
        <DialogContent className="sm:max-w-md" data-testid={`${testIdPrefix}-pause-dialog`}>
          <DialogHeader>
            <DialogTitle>Pause recurring service?</DialogTitle>
            <DialogDescription>
              {pauseFor && (
                <>Only <b>{pauseFor.label || "this property"}</b>{" "}
                (<span className="text-gray-600">{pauseFor.address}</span>) will
                be paused. Other properties on this customer&apos;s account are
                not affected.</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 grid gap-2">
            <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-3">
              <div className="text-[13px] font-extrabold text-gray-900">Pick a resume date</div>
              <div className="text-[11px] text-gray-600">Service auto-resumes on this date and the next visit is generated automatically.</div>
              <input type="date"
                     min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                     value={pauseForm.resume_date}
                     onChange={(e) => setPauseForm((s) => ({ ...s, resume_date: e.target.value }))}
                     className="mt-2 w-full h-11 px-3 rounded-lg border-2 border-emerald-300 text-sm font-bold text-gray-900 bg-white focus:outline-none focus:border-emerald-500"
                     data-testid={`${testIdPrefix}-pause-date-input`}
                     required />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={closePause}
                    data-testid={`${testIdPrefix}-pause-cancel`}>Cancel</Button>
            <Button
              onClick={submitPause}
              disabled={busy === "pause" || !pauseForm.resume_date}
              className="rounded-full bg-amber-700 hover:bg-amber-800 disabled:bg-amber-300 text-white"
              data-testid={`${testIdPrefix}-pause-confirm`}
            >
              {busy === "pause" && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
              Confirm Pause
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="sm:max-w-md" data-testid={`${testIdPrefix}-dialog`}>
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Add property" : "Edit property"}</DialogTitle>
            <DialogDescription>Each property carries its own address, lawn size, and price.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name (optional)</Label>
              <Input value={form.label} onChange={(e) => setForm((s) => ({ ...s, label: e.target.value }))}
                     placeholder="Home, Rental, Property #2…"
                     data-testid={`${testIdPrefix}-form-label`} />
            </div>
            <div>
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm((s) => ({ ...s, address: e.target.value }))}
                     placeholder="123 Main St Jacksonville FL"
                     data-testid={`${testIdPrefix}-form-address`} />
            </div>
            <div>
              <Label>Lawn size (sq ft)</Label>
              <Input type="number" min="0" value={form.square_feet}
                     onChange={(e) => setForm((s) => ({ ...s, square_feet: e.target.value }))}
                     placeholder="8500"
                     data-testid={`${testIdPrefix}-form-sqft`} />
            </div>
            <div>
              <Label>Yard area</Label>
              <div className="mt-1 grid grid-cols-3 gap-2">
                {YARD_OPTIONS.map((o) => (
                  <button key={o.key} type="button"
                          onClick={() => setForm((s) => ({ ...s, yard_area: o.key }))}
                          className={`rounded-lg border-2 py-2 text-xs font-extrabold ${
                            form.yard_area === o.key
                              ? "border-[#047857] bg-emerald-50 text-[#047857]"
                              : "border-gray-200 text-gray-600"
                          }`}
                          data-testid={`${testIdPrefix}-form-yard-${o.key}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={closeDialog}>Cancel</Button>
            <Button
              onClick={save} disabled={busy === "save"}
              className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white"
              data-testid={`${testIdPrefix}-save`}
            >
              {busy === "save" && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
              {editing === "new" ? "Add property" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter259 — Full "Add Property & Book Service" workflow */}
      <AdminAddPropertyBookingDialog
        userId={userId}
        open={addBookOpen}
        onOpenChange={setAddBookOpen}
        onCreated={() => refresh()}
      />

    </div>
  );
}
